import { GridColDef } from '@mui/x-data-grid-premium';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import React, { useEffect } from 'react';
import { tableSettingsProps, useTableSettings } from 'src/core/tables/useTableSettings';
import tableHelper from 'src/core/tables/tableHelper';
import CustomButton from 'src/core/ui/CustomButton';
import { useGetReceivingOrdersByWarehouse } from 'src/pages/warehouses/warehouseRequests';

// Third tuple item exposes the source rows and status without a second
// API request. Existing callers that destructure just two items still work.
export const useReceivingOrdersTable = (warehouseId: number | string | null, refreshKey?: unknown, onSelect?: (row: ReceivingOrderRow) => void): [columns: GridColDef[], tableSettings: tableSettingsProps, data: ReceivingOrdersTableData] => {
    const { rows: responseRows, status } = useGetReceivingOrdersByWarehouse(warehouseId, refreshKey);
    const rows: ReceivingOrderRow[] = Array.isArray(responseRows) ? responseRows : emptyReceivingRows;

    const columns = React.useMemo<GridColDef[]>(() => [
        { field: 'actions', type: 'actions', headerName: 'View', width: 80, getActions: params => tableHelper.buttonColumn(params, 'View', `/warehouse/receiving-orders/edit?warehouseId=${encodeURIComponent(String(warehouseId))}&id=`, 'ReceivingAdviceId', 'View Receiving Order') },
        ...(onSelect ? [{ field: 'select', headerName: '', width: 80, sortable: false, filterable: false, disableColumnMenu: true, disableExport: true, renderCell: params => <CustomButton iconButton size="small" title="Load receiving row" tooltipText="Load this row into the receiving form. Receive saves it immediately." icon={<ArrowUpwardIcon />} onClick={(event) => { event.stopPropagation(); onSelect(params.row as ReceivingOrderRow); }} /> } as GridColDef] : []),
        { field: 'ReceivingAdviceRowId', headerName: 'Receiving Advice Row Id' },
        { field: 'ProductId', headerName: 'Product Id' },
        { field: 'ProductName', headerName: 'Product', flex: 1, minWidth: 200 },
        { field: 'QuantityExpected', headerName: 'Quantity Expected', type: 'number' },
        { field: 'ReceivedQty', headerName: 'Quantity Received', type: 'number', valueGetter: (_, row) => row.ReceivedQty ?? row.QuantityReceive },
        { field: 'IsReceived', headerName: 'Received', type: 'boolean' },
        { field: 'StorageItemId', headerName: 'Storage Item Id' },
        { field: 'ManufacturerProductId', headerName: 'Manufacturer Product Id' },
        { field: 'EANCode', headerName: 'Barcode(EAN)', type: 'string' },
        { field: 'CreateDate', headerName: 'Create Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
        { field: 'DeliveryNoteRef', headerName: 'Delivery Note Ref' },
        { field: 'ReceivingAdviceId', headerName: 'Receiving Advice Id' },
        { field: 'WarehouseId', headerName: 'Warehouse Id' },
        { field: 'ExternalDocumentId', headerName: 'External Document Id' },
        receivingAdviceTypeColumn,
        { field: 'Sender', headerName: 'Sender', flex: 1, minWidth: 160 },
        { field: 'SenderNote', headerName: 'Sender Note', flex: 1, minWidth: 160 },
        { field: 'ItemId', headerName: 'Item Id' },
    ], [onSelect, warehouseId]);

    const tableName = 'ReceivingOrdersTable';
    const tableSettings = useTableSettings({ tableName, defaultColumnVisibilityModel: { StorageItemId: false, WarehouseId: false, ReceivingAdviceTypeId: true, ItemId: false }, columns, idPath: receivingRowIdPath, rows, status });

    return [columns, tableSettings, { rows, status }];
};

export const useReceivingOrderMatchesTable = (rows: ReceivingOrderRow[], onSelect: (row: ReceivingOrderRow) => void, status: ReceivingOrdersTableData['status']): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
    const columns = React.useMemo<GridColDef[]>(() => [
        { field: 'ReceivingAdviceId', headerName: 'Receiving Order ID', minWidth: 160 },
        { field: 'ReceivingAdviceRowId', headerName: 'Receiving Row ID', minWidth: 150 },
        receivingAdviceTypeColumn,
        { field: 'Sender', headerName: 'Sender', minWidth: 180, flex: 1 },
        { field: 'ProductId', headerName: 'Product ID', minWidth: 120 },
        { field: 'ManufacturerProductId', headerName: 'Manufacturer Product ID', minWidth: 200 },
        { field: 'ProductName', headerName: 'Product', minWidth: 180, flex: 1 },
        { field: 'QuantityExpected', headerName: 'Expected', type: 'number', minWidth: 100 },
        { field: 'QuantityReceive', headerName: 'Received Qty', type: 'number', minWidth: 120, valueGetter: (_, row) => row.ReceivedQty ?? row.QuantityReceive },
        { field: 'EANCode', headerName: 'Barcode(EAN)', minWidth: 150 },
        { field: 'IsReceived', headerName: 'Received', type: 'boolean', minWidth: 100 },
        { field: 'DeliveryNoteRef', headerName: 'Delivery Note Ref', minWidth: 160 },
        { field: 'SenderNote', headerName: 'Sender Note', minWidth: 180, flex: 1 },
    ], []);

    const tableSettings = useTableSettings({ columns, idPath: receivingRowIdPath, rows, tableName: 'ReceivingOrderMatchesTable', status });
    tableSettings.hasToolbar = false;
    tableSettings.autoHeight = true;

    useEffect(() => {
        const selection = tableSettings.rowSelectionModel ?? [];
        if (!Array.isArray(selection) || selection.length === 0) return;
        const selectedId = selection[0];
        const selectedRow = rows.find((row) => String(row.ReceivingAdviceRowId) === String(selectedId));
        if (!selectedRow) return;
        tableSettings.setRowSelectionModel?.([]);
        onSelect(selectedRow);
    }, [tableSettings.rowSelectionModel, rows, onSelect]);

    return [columns, tableSettings];
};

export const useReceivingOrderRowsTable = (receivingAdvice: any, status: string | number | null, loading = false): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
    const rows = Array.isArray(receivingAdvice?.ReceivingAdviceRows) ? receivingAdvice.ReceivingAdviceRows : emptyReceivingRows;
    const idPath = ['Id'];

    const columns: GridColDef[] = React.useMemo(() => [
        { field: 'Id', headerName: 'Row Id' },
        { field: 'ExternalDocumentRowId', headerName: 'External Document Row Id' },
        { field: 'ProductId', headerName: 'Product Id' },
        { field: 'ProductName', headerName: 'Product', flex: 1, minWidth: 220 },
        { field: 'Quantity', headerName: 'Quantity', type: 'number' },
        { field: 'IsReceived', headerName: 'Received', type: 'boolean' },
        { field: 'StorageValue', headerName: 'Storage Value', type: 'number' },
        { field: 'ETA', headerName: 'ETA', type: 'dateTime', valueGetter: tableHelper.getDateColumn },
    ], []);

    const tableName = 'ReceivingOrderRowsTable';
    const tableSettings = useTableSettings({ columns, idPath, rows, tableName, status: status as string });
    tableSettings.hasToolbar = false;
    tableSettings.autoHeight = true;
    tableSettings.hideFooter = true;
    tableSettings.loading = loading;

    return [columns, tableSettings];
};

export const receivingAdviceTypes: Record<string, string> = { '1': 'Purchase Order', '2': 'Transfer Order', '3': 'Return To Warehouse Order' };
const receivingAdviceTypeColumn: GridColDef = { field: 'ReceivingAdviceTypeId', headerName: 'Receiving Advice Type', minWidth: 220, valueFormatter: value => receivingAdviceTypes[String(value)] ?? String(value ?? '') };

export type ReceivingOrderRow = {
    ReceivingAdviceRowId: number | string;
    ReceivingAdviceId: number | string;
    WarehouseId: number | string;
    ProductId?: number | string | null;
    StorageItemId?: number | string | null;
    ReceivingAdviceTypeId?: number | string | null;
    ManufacturerProductId?: number | string | null;
    ProductName?: string | null;
    Sender?: string | null;
    SenderNote?: string | null;
    QuantityExpected?: number | null;
    QuantityReceive?: number | null;
    ReceivedQty?: number | null;
    EANCode?: number | string | null;
    ManufacturerAltProductId?: number | string | null;
    IsReceived?: boolean;
    DeliveryNoteRef?: string | null;
    [field: string]: unknown;
};

const emptyReceivingRows: ReceivingOrderRow[] = [];
const receivingRowIdPath = ['ReceivingAdviceRowId'];

export type ReceivingOrdersTableData = {
    rows: ReceivingOrderRow[];
    status: ReturnType<typeof useGetReceivingOrdersByWarehouse>['status'];
};

